import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IBankCashAccount } from '@app/modules/system-settings/system-setup/banking-info/bank-detail/bank-cash-accounts/bank-cash-accounts.models';
import { AddBankModel } from '@app/modules/system-settings/system-setup/banking-info/models/bank-add.model';
import { BankListItem, BankListRequestModel } from '@app/modules/system-settings/system-setup/banking-info/models/bank-list-item.model';
import { IEditService, IListService } from '@app/services';
import { BankEntity } from '../models/bank';

@Injectable()
export class BankService implements IListService, IEditService<BankEntity> {
	endpointBase = '/api/banks';
	endpointBaseCF = '/api/banks-cf';

	constructor(private readonly http: HttpClient) {}

	// IListService implementation
	getList(): Observable<BankListItem[]> {
		return this.getBankList({
			HasCashAccountsOnly: false,
			ActiveOnly: false,
			IncludeCurrentBalance: true,
			IncludeCanDelete: true,
		});
	}

	// IEditService implementation
	getById(id: number): Observable<BankEntity> {
		return this.http.get<BankEntity>(`${this.endpointBaseCF}/${id}`);
	}

	// IEditService implementation
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	createEntity(entity: BankEntity): Observable<BankEntity> {
		throw new Error('Method not implemented.');
	}

	// IEditService implementation
	updateEntity(entity: BankEntity): Observable<BankEntity> {
		return this.http.put<BankEntity>(`${this.endpointBaseCF}`, entity);
	}

	// IListService, IEditService implementation
	deleteEntity(id: number | string): Observable<void> {
		return this.http.delete<void>(`${this.endpointBaseCF}/${id}`);
	}

	public getBankList(model: BankListRequestModel): Observable<BankListItem[]> {
		return this.http.get<BankListItem[]>(`${this.endpointBaseCF}`, {
			params: {
				HasCashAccountsOnly: model.HasCashAccountsOnly,
				ActiveOnly: model.ActiveOnly,
				IncludeCurrentBalance: model.IncludeCurrentBalance,
				IncludeCanDelete: model.IncludeCanDelete,
			},
		});
	}

	public getBanks(cashOnly: boolean, activeOnly: boolean): Observable<any> {
		return this.http.get(`${this.endpointBase}`, {
			params: {
				cashOnly,
				activeOnly,
			},
		});
	}

	public getDefaultBankId(): Observable<number> {
		return this.http.get<number>(`${this.endpointBaseCF}/get-default-bank-id`);
	}

	public setDefaultBankId(bankId: number): Observable<void> {
		const params = new HttpParams().set('id', bankId.toString());
		return this.http.put<void>(`${this.endpointBaseCF}/set-default-bank-id`, null, { params });
	}

	public getCheckProcessingInfo(bankId: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/${bankId}/print-lock-data`);
	}

	public cancelPrintChecksSession(bankId: number, hcmProcessGuid: string, fmProcessId: number): Observable<any> {
		return this.http.post(`${this.endpointBase}/${bankId}/clear-print-lock`, {
			HCMProcessGuid: hcmProcessGuid,
			FMProcessId: fmProcessId,
			BankId: bankId,
		});
	}

	public addBank(bank: AddBankModel): Observable<number> {
		return this.http.post<number>(`${this.endpointBaseCF}`, bank);
	}

	public getBankCashAccounts(bankId: number): Observable<IBankCashAccount[]> {
		return this.http.get<IBankCashAccount[]>(`${this.endpointBaseCF}/${bankId}/cash-accounts`);
	}

	public updateBankCashAccounts(bankId: number, accounts: IBankCashAccount[]): Observable<number> {
		return this.http.put<number>(`${this.endpointBaseCF}/${bankId}/cash-accounts`, accounts);
	}

	// endingDate is currently being passed as a string
	public getBankBookBalance(id: number, endingDate: any): Observable<any> {
		return this.http.get(this.endpointBase + '/' + id + '/book-balance', {
			params: { endingDate: endingDate },
		});
	}
}
